import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchWarehouseSubrentalsPage,
  viewerTimeZone,
  warehouseQueryKeys,
  type WarehouseSubrentalRow,
  type WarehouseSubrentalsParams,
} from '@/data/warehouse';
import { fetchVendorOptions } from '@/data/procurement';
import { ALL_SITE_FILTER, summarizeSiteFilterSelection } from '@/domain/library';
import { formatCount, formatDay, formatDayRange, formatDueIn, formatMoney, pluralize } from '@/domain/warehouse';
import SearchableMultiSelect, { type MultiSelectOption } from '../../../components/SearchableMultiSelect';
import StageFilterToolbar from '../../../components/events/StageFilterToolbar';
import type { GroupedTableColumn } from '../../../components/common/GroupedDataTable';
import LibraryRecordCard, { LibraryRecordCardDetail } from '../../../components/common/LibraryRecordCard';
import MaintenanceActionsDropdown, { type MaintenanceAction } from '../../../components/admin/MaintenanceActionsDropdown';
import { LateDate, SourceTag, StatusChip } from '../../../components/status/StatusChip';
import LibraryListToolbar, { type LibraryListSortOption } from '../../../components/library/LibraryListToolbar';
import LibraryListBody from '../../../components/library/LibraryListBody';
import WarehouseHero, { type WarehouseHeroStat } from './WarehouseHero';
import WarehouseMeter from './WarehouseMeter';
import SubrentalsCalendarView from './SubrentalsCalendarView';
import { useWarehouseListState, type WarehouseListConfig, type WarehouseViewMode } from './useWarehouseListState';
import { buildStatusFilterModel } from './warehouseStatusFilters';
import { FOLD_TAIL, warehouseCountText } from './warehouseListText';
import { warehouseRecordHref } from './warehouseTabs';
import { WAREHOUSE_PAGE_SIZE, type WarehouseTabContext } from './shared';

const STATUS_MODEL = buildStatusFilterModel('subrental');
const VIEWS: readonly WarehouseViewMode[] = ['list', 'calendar'];
const VIEW_OPTIONS = [
  { value: 'list' as const, label: 'List' },
  { value: 'calendar' as const, label: 'Calendar' },
];
const KIND_LABELS: { [kind in WarehouseSubrentalRow['kind']]: string } = {
  event: 'Event',
  shared_stock: 'Shared stock',
  vehicle: 'Vehicle',
};
const SORT_OPTIONS: readonly LibraryListSortOption[] = [
  { value: 'window', label: 'Rental window' },
  { value: 'due_back', label: 'Return date' },
  { value: 'doc', label: 'Order number' },
  { value: 'status', label: 'Status' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'cost', label: 'Cost' },
  { value: 'updated', label: 'Last update' },
];
const DESCENDING_FIRST = ['updated', 'cost'];
const NOUN = { singular: 'subrental', plural: 'subrentals' };
// "Late" holds every late Portal subrental too, so it carries no "up to 30 days" hint here.
const GROUP_HINTS = { out_now: 'On rent', drafts: 'not sent yet' };
/** Portal subrentals that are still open on the vendor's side. */
const OPEN_ON_VENDOR = new Set(['submitted', 'acknowledged', 'on_rent', 'partially_received', 'return_overdue']);

export const SUBRENTALS_DEFAULT_CONFIG: WarehouseListConfig = {
  search: '',
  sites: [ALL_SITE_FILTER],
  facet: 'all',
  statuses: STATUS_MODEL.defaults,
  chips: [],
  grouping: 'date',
  fold: true,
  sortKey: 'window',
  sortDir: 'asc',
  view: 'list',
  dateFrom: '',
  dateTo: '',
  secondDateFrom: '',
  secondDateTo: '',
  vendorIds: [],
  orderedBy: '',
};

type SubrentalsTabProps = {
  context: WarehouseTabContext;
  /** Edit on maintenance.xrental_orders: create, receive and return (managers only view). */
  canEdit: boolean;
  onNewSubrental: () => void;
  onScanToReceive?: () => void;
};

function unitsCell(row: WarehouseSubrentalRow): React.ReactNode {
  if (row.kind === 'vehicle') return pluralize(Math.max(1, row.unitsTotal), 'vehicle');
  return <WarehouseMeter done={row.unitsIn} total={row.unitsTotal} suffix="in" />;
}

function unitsText(row: WarehouseSubrentalRow): string {
  if (row.kind === 'vehicle') return pluralize(Math.max(1, row.unitsTotal), 'vehicle');
  return `${formatCount(row.unitsIn)} of ${formatCount(row.unitsTotal)} in`;
}

function costMain(row: WarehouseSubrentalRow): string {
  if (row.costMode === 'per_day' && row.costPerDay != null) return `${formatMoney(row.costPerDay)} / day`;
  return formatMoney(row.costAmount) ?? '—';
}

function costSub(row: WarehouseSubrentalRow): string | null {
  if (row.costMode === 'per_day') {
    const days = row.costDays != null ? pluralize(row.costDays, 'day') : null;
    if (row.costIsEstimate) return days ? `Estimate, ${days}` : 'Estimate';
    return [days, formatMoney(row.costAmount)].filter(Boolean).join(' · ') || null;
  }
  return row.costIsEstimate ? 'Estimate' : 'Order total';
}

function windowNote(row: WarehouseSubrentalRow): string | null {
  if (row.extended) return 'Extended once';
  if (row.status === 'on_rent' && row.dueBackInDays != null && row.dueBackInDays >= 0) {
    return `Back to the vendor ${formatDueIn(row.dueBackInDays)}`;
  }
  if (row.expectedDay && row.unitsIn < row.unitsTotal) return `Expected ${formatDay(row.expectedDay)}`;
  return null;
}

export default function SubrentalsTab({ context, canEdit, onNewSubrental, onScanToReceive }: SubrentalsTabProps) {
  const navigate = useNavigate();
  const state = useWarehouseListState({
    tab: 'subrentals',
    profileId: context.profileId,
    prefs: context.prefs,
    prefsLoaded: context.prefsLoaded,
    defaults: SUBRENTALS_DEFAULT_CONFIG,
    views: VIEWS,
    allStatuses: STATUS_MODEL.statuses,
    siteOptionIds: context.siteOptionIds,
    regions: context.regionFilterOptions,
    scopeKey: context.linkedEvent?.id ?? null,
    descendingFirst: DESCENDING_FIRST,
  });
  const { config, update, page, setPage, sortFor, setGrouping } = state;
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const vendorsQuery = useQuery({
    queryKey: ['warehouse', 'vendor-options'],
    queryFn: fetchVendorOptions,
    enabled: advancedOpen || sheetOpen || config.vendorIds.length > 0,
    staleTime: 5 * 60_000,
  });
  const vendorOptions = React.useMemo<MultiSelectOption[]>(
    () => (vendorsQuery.data ?? []).map((vendor) => ({ id: vendor.vendor_id, label: vendor.name })),
    [vendorsQuery.data],
  );

  const pastExpectedChip = config.chips.includes('past_expected');
  const params = React.useMemo<WarehouseSubrentalsParams>(() => ({
    search: state.debouncedSearch,
    locationIds: state.locationIds,
    statuses: state.statusesParam,
    chips: config.chips,
    eventId: context.linkedEvent?.id ?? null,
    includeFold: !config.fold || state.showFolded || pastExpectedChip,
    groupBy: config.grouping,
    sortKey: config.sortKey,
    sortDir: config.sortDir,
    offset: (page - 1) * WAREHOUSE_PAGE_SIZE,
    limit: WAREHOUSE_PAGE_SIZE,
    timezone: viewerTimeZone(),
    kind: config.facet === 'equipment' || config.facet === 'vehicles' ? config.facet : 'all',
    windowFrom: config.dateFrom || null,
    windowTo: config.dateTo || null,
    vendorIds: config.vendorIds,
  }), [config, context.linkedEvent, page, pastExpectedChip, state.debouncedSearch, state.locationIds, state.showFolded, state.statusesParam]);

  const pageQuery = useQuery({
    queryKey: warehouseQueryKeys.subrentals(params),
    queryFn: () => fetchWarehouseSubrentalsPage(params),
    enabled: !state.noStatuses,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const data = state.noStatuses ? undefined : pageQuery.data;
  const stats = data?.stats ?? {};
  const chipCounts = data?.chipCounts ?? {};
  const total = data?.total ?? 0;
  const loading = !state.noStatuses && pageQuery.isLoading && !data;
  const statValue = (key: string) => (data ? formatCount(stats[key] ?? 0) : '…');

  const heroStats: WarehouseHeroStat[] = [
    { key: 'open', label: 'Open subrentals', value: statValue('open'), hint: 'Equipment and vehicles rented from vendors' },
    { key: 'waiting', label: 'Waiting on vendor', value: statValue('waiting_on_vendor'), hint: 'Requested, not confirmed yet' },
    { key: 'partial', label: 'Partially received', value: statValue('partially_received'), hint: 'Some units still due from the vendor' },
    {
      key: 'past_expected',
      label: 'Past expected date',
      value: statValue('past_expected'),
      hint: data ? `${formatCount(stats.past_expected_over_30 ?? 0)} are more than 30 days late` : null,
      tone: (stats.past_expected ?? 0) > 0 ? 'warn' : null,
    },
  ];

  const handleGroupingChange = React.useCallback(function handleGroupingChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setGrouping(event.target.value);
  }, [setGrouping]);
  const handleSortChange = React.useCallback(function handleSortChange(sortKey: string, sortDir: 'asc' | 'desc') {
    update({ sortKey, sortDir });
  }, [update]);

  const rowActions = React.useCallback((row: WarehouseSubrentalRow): MaintenanceAction[] => {
    const href = warehouseRecordHref.subrental(row.id);
    const actions: MaintenanceAction[] = [{ key: 'open', label: 'Open subrental', onSelect: () => navigate(href) }];
    // R2 cross-rentals are read-only until cutover; the record says why.
    if (!canEdit || row.source !== 'portal' || !OPEN_ON_VENDOR.has(row.status)) return actions;
    // The record's own rule: a vehicle is never received, and goes back while it is out (from Lines,
    // since a vehicle order has no Units section).
    const vehicle = row.kind === 'vehicle';
    if (!vehicle && row.unitsIn < row.unitsTotal) {
      actions.push({
        key: 'receive',
        label: 'Receive from vendor',
        onSelect: () => navigate(`${warehouseRecordHref.subrental(row.id, 'units')}?receive=1`),
      });
    }
    if (vehicle || row.unitsIn - row.unitsReturned > 0) {
      actions.push({
        key: 'return',
        label: 'Return to vendor',
        onSelect: () => navigate(`${warehouseRecordHref.subrental(row.id, vehicle ? 'lines' : 'units')}?return=1`),
      });
    }
    return actions;
  }, [canEdit, navigate]);
  const renderMenu = (row: WarehouseSubrentalRow) => (
    <MaintenanceActionsDropdown menuOnly menuLabel={`Actions for ${row.docNo ?? 'subrental'}`} actions={rowActions(row)} />
  );

  const columns: GroupedTableColumn<WarehouseSubrentalRow>[] = [
    {
      key: 'status',
      header: 'Status',
      width: '9.5rem',
      sort: sortFor('status'),
      render: (row) => <StatusChip kind="subrental" status={row.status} />,
    },
    {
      key: 'subrental',
      header: 'Subrental',
      sort: sortFor('doc'),
      render: (row) => (
        <>
          <span className="library-list-doc">
            <Link className="library-list-doc__link" to={warehouseRecordHref.subrental(row.id)}>{row.docNo ?? 'Subrental'}</Link>
            {row.source === 'r2' ? <SourceTag source="r2" /> : null}
            <span className="warehouse-kind-tag">{KIND_LABELS[row.kind]}</span>
          </span>
          <span className="grouped-data-table__sub">
            {[row.vendor?.name ?? 'No vendor', row.lineCount > 1 ? pluralize(row.lineCount, 'line') : row.itemSummary]
              .filter(Boolean)
              .join(' · ')}
            {row.site?.name ? <span className="grouped-data-table__narrow-only">{` · ${row.site.name}`}</span> : null}
          </span>
        </>
      ),
    },
    {
      key: 'site',
      header: 'Site',
      optional: true,
      render: (row) => row.site?.name ?? <span className="fg-muted">{'—'}</span>,
    },
    {
      key: 'event',
      header: 'Event',
      render: (row) => {
        if (row.event && row.eventCount <= 1) {
          return (
            <>
              <Link className="library-list-doc__link" to={warehouseRecordHref.event(row.event.id)}>{row.event.code ?? 'Event'}</Link>
              {row.event.name ? <span className="grouped-data-table__sub">{row.event.name}</span> : null}
            </>
          );
        }
        if (row.eventCount > 1) {
          return <span>{`${pluralize(row.unitsWithEvents, 'unit')} with ${pluralize(row.eventCount, 'event')}`}</span>;
        }
        return <span className="fg-muted">{'—'}</span>;
      },
    },
    {
      key: 'window',
      header: 'Rental window',
      width: '11rem',
      sort: sortFor('window'),
      render: (row) => {
        const note = windowNote(row);
        return (
          <>
            <span className="num">{formatDayRange(row.windowStartDay, row.windowEndDay) ?? 'No dates'}</span>
            {note || row.daysLate ? (
              <span className="grouped-data-table__sub">
                {note}
                {row.daysLate ? <>{note ? ' · ' : ''}<LateDate daysLate={row.daysLate} /></> : null}
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'units',
      header: 'Units',
      width: '8rem',
      render: (row) => <span className="num">{unitsCell(row)}</span>,
    },
    {
      key: 'cost',
      header: 'Cost',
      width: '9rem',
      align: 'end',
      sort: sortFor('cost'),
      render: (row) => {
        const sub = costSub(row);
        return (
          <>
            <span className="num">{costMain(row)}</span>
            {sub ? <span className="grouped-data-table__sub">{sub}</span> : null}
          </>
        );
      },
    },
    {
      key: 'menu',
      header: <span className="sr-only">Actions</span>,
      width: '3rem',
      className: 'grouped-data-table__menu-cell',
      render: renderMenu,
    },
  ];

  const serverFold = data?.fold ?? null;
  const foldCopy = config.fold && !pastExpectedChip && serverFold ? {
    title: 'More than 30 days late',
    body: `${formatCount(serverFold.count)} R2 cross-rental orders are still open more than 30 days after their rental window ended. ${FOLD_TAIL}`,
  } : null;

  const isCalendar = config.view === 'calendar';
  const openOnly = !state.statusFilterActive;
  const countText = isCalendar
    ? (loading ? 'Loading subrentals…' : `${formatCount(total)} ${openOnly ? 'open ' : ''}${total === 1 ? 'subrental' : 'subrentals'} match`)
    : warehouseCountText({ loading, page, pageSize: WAREHOUSE_PAGE_SIZE, total, noun: NOUN, openOnly, fold: serverFold });
  const sortLabel = (SORT_OPTIONS.find((option) => option.value === config.sortKey)?.label ?? config.sortKey).toLowerCase();

  const renderCard = (row: WarehouseSubrentalRow) => {
    const href = warehouseRecordHref.subrental(row.id);
    return (
      <LibraryRecordCard
        key={row.id}
        href={href}
        onOpen={() => navigate(href)}
        code={row.docNo ?? 'Subrental'}
        badges={(
          <>
            {row.source === 'r2' ? <SourceTag source="r2" className="library-record-card__pill" /> : null}
            <StatusChip kind="subrental" status={row.status} size="sm" className="library-record-card__pill" />
          </>
        )}
        title={row.vendor?.name ?? 'No vendor'}
        details={(
          <>
            <LibraryRecordCardDetail>{[KIND_LABELS[row.kind], row.itemSummary, row.site?.name].filter(Boolean).join(' · ')}</LibraryRecordCardDetail>
            <LibraryRecordCardDetail muted>
              {formatDayRange(row.windowStartDay, row.windowEndDay) ?? 'No dates'}
              {row.daysLate ? <> <LateDate daysLate={row.daysLate} /></> : null}
            </LibraryRecordCardDetail>
          </>
        )}
        meta={[unitsText(row), costMain(row), row.event?.code ?? null]}
        actions={renderMenu(row)}
      />
    );
  };

  const siteControl = (
    <SearchableMultiSelect
      options={context.siteOptions}
      selected={config.sites}
      onChange={state.setSites}
      placeholder="All sites"
      ariaLabel="Filter by site"
      className="events-site-filter"
      selectionSummary={summarizeSiteFilterSelection(config.sites, context.siteOptions, 'All sites')}
      showPills={false}
    />
  );
  const statusControl = (
    <StageFilterToolbar
      selectedStatuses={state.selectedStatusSet}
      onSelectedStatusesChange={state.setStatuses}
      stageGroups={STATUS_MODEL.stageGroups}
      standaloneStatuses={[]}
      orderStageLookup={STATUS_MODEL.lookup}
      defaultSelectedStatuses={STATUS_MODEL.defaults}
      label="Status"
      pluralNoun="statuses"
      allLabel="All statuses"
      defaultSelectionLabel="Open"
      compact
    />
  );

  return (
    <>
      <div className="settings-page__pinned">
        <WarehouseHero
          isPhone={context.isPhone}
          statsLabel="Subrental summary"
          stats={heroStats}
          actions={(
            <>
              {canEdit ? <button type="button" className="btn btn-primary" onClick={onNewSubrental}>New subrental</button> : null}
              {onScanToReceive ? <button type="button" className="btn" onClick={onScanToReceive}>Scan to receive</button> : null}
            </>
          )}
        />
        {context.tabStrip}
        <LibraryListToolbar
          search={config.search}
          onSearchChange={(value) => update({ search: value })}
          searchPlaceholder="Subrental #, vendor, item, SKU or event"
          searchPlaceholderShort="Subrental #, vendor or item"
          searchLabel="Search subrentals"
          siteControl={siteControl}
          siteFilterActive={state.siteFilterActive}
          facetLabel="What is rented"
          facetOptions={[
            { value: 'all', label: 'All' },
            { value: 'equipment', label: 'Equipment' },
            { value: 'vehicles', label: 'Vehicles' },
          ]}
          facet={config.facet}
          onFacetChange={(value) => update({ facet: value })}
          statusControl={statusControl}
          statusFilterActive={state.statusFilterActive}
          quickFilters={[
            { key: 'past_expected', label: 'Past expected date', count: chipCounts.past_expected ?? null, active: pastExpectedChip, tone: 'warn' },
            { key: 'due_back_this_week', label: 'Due back to vendor this week', count: chipCounts.due_back_this_week ?? null, active: config.chips.includes('due_back_this_week') },
            { key: 'linked_to_event', label: 'Linked to an event', count: null, active: config.chips.includes('linked_to_event') },
          ]}
          onToggleQuickFilter={state.toggleChip}
          extraChip={context.linkedEvent ? { label: `Linked to ${context.linkedEvent.code ?? 'event'}`, onClear: context.onClearLinkedEvent } : null}
          countText={countText}
          viewOptions={VIEW_OPTIONS}
          view={config.view}
          onViewChange={(view) => update({ view })}
          viewAriaLabel="Warehouse view"
          advancedOpen={advancedOpen}
          onToggleAdvanced={() => setAdvancedOpen((open) => !open)}
          advancedActiveCount={state.advancedActiveCount}
          advancedFields={(
            <>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Rental window</span>
                <div className="library-toolbar__range">
                  <input type="date" aria-label="Window from" value={config.dateFrom} onChange={(event) => update({ dateFrom: event.target.value })} />
                  <span aria-hidden="true">to</span>
                  <input type="date" aria-label="Window to" value={config.dateTo} onChange={(event) => update({ dateTo: event.target.value })} />
                </div>
              </div>
              <div className="library-toolbar__field text-xxs fg-muted">
                <span>Vendor</span>
                <SearchableMultiSelect
                  options={vendorOptions}
                  selected={config.vendorIds}
                  onChange={state.setVendorIds}
                  placeholder={vendorsQuery.isLoading ? 'Loading vendors…' : 'All vendors'}
                  ariaLabel="Filter by vendor"
                  showPills={false}
                />
              </div>
              <label className="library-toolbar__field text-xxs fg-muted">
                <span>Grouping</span>
                <select className="xs" value={config.grouping} onChange={handleGroupingChange} aria-label="Group rows by">
                  <option value="off">Off</option>
                  <option value="date">Rental window</option>
                  <option value="status">Status</option>
                  <option value="vendor">Vendor</option>
                  <option value="site">Site</option>
                </select>
              </label>
              <div className="library-toolbar__field text-xxs fg-muted warehouse-fold-toggle">
                <span>Stale R2 cross-rentals</span>
                <label className="warehouse-fold-toggle__control">
                  <input type="checkbox" checked={config.fold} onChange={(event) => update({ fold: event.target.checked })} />
                  Fold R2 orders more than 30 days late
                </label>
              </div>
            </>
          )}
          sortOptions={SORT_OPTIONS}
          sortKey={config.sortKey}
          sortDir={config.sortDir}
          onSortChange={handleSortChange}
          savedViews={state.savedViews}
          onResetAdvanced={state.resetAdvanced}
          onResetAll={state.resetAll}
          compact={context.compactToolbar}
          sheetResultLabel={loading ? 'Show subrentals' : `Show ${formatCount(total)} ${total === 1 ? 'subrental' : 'subrentals'}`}
          onSheetOpenChange={setSheetOpen}
        />
      </div>

      <main className="settings-content warehouse-content">
        <LibraryListBody
          panelId={context.panelId}
          labelledBy={context.tabElementId('subrentals')}
          noun={NOUN}
          view={config.view}
          isPhone={context.isPhone}
          data={data}
          loading={loading}
          error={state.noStatuses ? null : pageQuery.error}
          onRetry={() => { void pageQuery.refetch(); }}
          blockedMessage={state.noStatuses ? 'Pick at least one status to see subrentals.' : null}
          columns={columns}
          grouped={config.grouping !== 'off'}
          groupHints={GROUP_HINTS}
          renderCard={renderCard}
          fold={foldCopy}
          onToggleFold={state.toggleFolded}
          emptyTitle="No subrentals match these filters"
          emptyHint={`Adjust the search or filters${canEdit ? ', or start a new subrental' : ''}.`}
          page={page}
          pageSize={WAREHOUSE_PAGE_SIZE}
          onPageChange={setPage}
          pageNote={config.grouping === 'off' ? `sorted by ${sortLabel}` : 'one row per order, not per rental line'}
          calendar={isCalendar && !state.noStatuses ? <SubrentalsCalendarView params={params} /> : undefined}
        />
      </main>
    </>
  );
}
